import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useWishlist } from '../../context/WishlistContext';
import { getImageUrl } from '../../utils/imageUrl';
import { trackFacebookEvent } from '../../utils/facebookPixel';
import { gtmEvents } from '../../utils/gtmDataLayer';
import QuickOrderModal from './QuickOrderModal';

import { Product } from '../../types';
import './ProductCard.css';

interface ProductCardProps {
  product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { t, i18n } = useTranslation();
  const { addItem: addToWishlist, removeItem: removeFromWishlist, isInWishlist } = useWishlist();
  const [inWishlist, setInWishlist] = useState(isInWishlist(product.id));
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isPortrait, setIsPortrait] = useState(true);
  const currentLang = i18n.language;

  // Quick-order modal visibility only — selections live inside the modal
  const [showModal, setShowModal] = useState(false);

  const primaryImage = getImageUrl(
    product.image ||
    product.primary_image ||
    product.images?.find(img => img.is_primary)?.image ||
    product.images?.[0]?.image
  );

  // Check whether the product has any attribute options
  const hasColors = !!(product.attributes?.colors?.enabled && (product.attributes.colors.options || []).length > 0);
  const hasSizes = !!(product.attributes?.sizes?.enabled && (product.attributes.sizes.options || []).length > 0);
  const hasWeights = !!(product.attributes?.weights?.enabled && (product.attributes.weights.options || []).length > 0);
  const hasCustom = !!(product.attributes?.custom && product.attributes.custom.some(cg => cg.enabled && (cg.options || []).length > 0));
  const hasAttributes = hasColors || hasSizes || hasWeights || hasCustom;

  const handleOrderClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (hasAttributes) {
      setShowModal(true);
    } else {
      // No attributes — straight to checkout via sessionStorage
      const orderData = { product, quantity: 1, selected_color: '', selected_size: '', selected_weight: '' };
      sessionStorage.setItem('directOrder', JSON.stringify(orderData));
      window.location.href = '/checkout';
    }
  };

  const handleWishlistToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const productName = currentLang === 'bn' ? product.name_bn : product.name_en;

    if (inWishlist) {
      removeFromWishlist(product.id);
      setInWishlist(false);
    } else {
      addToWishlist(product);
      setInWishlist(true);

      // Track Facebook AddToWishlist
      trackFacebookEvent('AddToWishlist', {
        content_name: productName,
        content_ids: [product.id.toString()],
        content_type: 'product',
        value: product.final_price,
        currency: 'BDT'
      });

      // Track GTM/GA4 add_to_wishlist
      gtmEvents.addToWishlist({
        id: product.id,
        name: productName,
        price: product.final_price,
      });
    }
  };

  return (
    <div className="product-card">
      <div className="product-image-container">
        <Link to={`/products/${product.slug}`} className="image-link">
          <img
            src={primaryImage}
            alt={currentLang === 'bn' ? product.name_bn : product.name_en}
            className={`product-image ${imageLoaded ? 'loaded' : ''} ${isPortrait ? 'portrait' : 'landscape'}`}
            onLoad={(e) => {
              const img = e.target as HTMLImageElement;
              const isPort = img.naturalHeight >= img.naturalWidth;
              setIsPortrait(isPort);
              setImageLoaded(true);
            }}
          />
          {!imageLoaded && <div className="image-skeleton"></div>}
        </Link>

        <button
          className={`wishlist-btn ${inWishlist ? 'active' : ''}`}
          onClick={handleWishlistToggle}
          aria-label="Add to Wishlist"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
        </button>

        {product.discount_percentage > 0 && (
          <span className="discount-tag">
            <span className="discount-number">{product.discount_percentage}%</span>
            <span className="discount-bengali">ছাড়</span>
          </span>
        )}

        {!product.in_stock && (
          <div className="stock-overlay">
            <span>{t('product.outOfStock')}</span>
          </div>
        )}
      </div>

      <div className="product-details">
        <Link to={`/products/${product.slug}`} className="product-title-link">
          <h3 className="product-title">
            {currentLang === 'bn' ? product.name_bn : product.name_en}
          </h3>
        </Link>

        <div className="product-footer">
          <div className="price-info">
            {product.discount_price ? (
              <>
                <span className="price">৳{product.discount_price.toLocaleString()}</span>
                <span className="original-price">৳{product.price.toLocaleString()}</span>
              </>
            ) : (
              <span className="price">৳{product.price.toLocaleString()}</span>
            )}
          </div>

          <button
            className="action-btn"
            onClick={handleOrderClick}
            disabled={!product.in_stock}
          >
            <span className="btn-text">{currentLang === 'bn' ? 'অর্ডার করুন' : 'Order Now'}</span>
          </button>
        </div>
      </div>

      {/* Quick-order modal — rendered via portal, owns its own selection state */}
      {showModal && (
        <QuickOrderModal product={product} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
};

export default ProductCard;
